import React, { useEffect, useMemo, useState } from 'react';
import { FilterBar, TicketsTable, TicketDetailPanel } from './components';
import type { QuickFilter } from './components';
import { CreateTicketModal } from '../shared';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import {
  AllTicketsRow,
  TicketDetail,
  ThreadMessage,
  PipelineStage,
} from './callCenterAllTicketsTypes';
import { useTicketsStore } from '../../../store/ticketsStore';
import {
  uiPriority,
  uiStatus,
  categoryToModule,
  relativeTime,
  formatDateTime,
} from '../shared/ticketAdapter';
import { Ticket, TicketMessage } from '../../../services/ticketsApi';
import { formatOrderId } from '../../../utils/formatId';
import styles from './CallCenterAllTickets.module.css';

function adaptRow(t: Ticket): AllTicketsRow {
  return {
    id: String(t.id),
    ticketId: `#${t.ticket_number}`,
    createdBy: t.created_by_name || 'System',
    module: categoryToModule(t.category),
    customer: t.customer_name || '—',
    priority: uiPriority(t.priority),
    status: uiStatus(t.status),
    assignedTo: t.assigned_to_name || 'Unassigned',
    lastUpdated: relativeTime(t.updated_at),
  };
}

function pipelineStage(t: Ticket): PipelineStage {
  if (t.status === 'closed') return 'Closed';
  if (t.status === 'resolved') return 'Resolved';
  if (t.status === 'in_progress' || t.status === 'waiting') return 'In Progress';
  if (t.assigned_to_user_id) return 'Assigned';
  return 'Open';
}

function adaptMessage(m: TicketMessage): ThreadMessage {
  const initials = (m.sender_name || 'U?')
    .split(/\s+/).slice(0, 2).map((n) => n[0]?.toUpperCase() ?? '').join('');
  return {
    id: String(m.id),
    senderInitials: initials,
    senderName: m.sender_name || 'Unknown',
    senderRole: m.sender_role === 'customer' ? 'Customer' : 'Support',
    timestamp: formatDateTime(m.created_at),
    body: m.body,
    avatarColor: m.sender_role === 'customer' ? 'orange' : 'blue',
  };
}

function adaptDetail(t: Ticket): TicketDetail {
  return {
    ticketId: `#${t.ticket_number}`,
    priority: uiPriority(t.priority),
    status: uiStatus(t.status),
    pipelineStage: pipelineStage(t),
    subject: t.subject,
    meta: `Related to: ${t.order_id ? formatOrderId(t.order_id) : '—'} · Customer: ${t.customer_name || '—'} · Created: ${formatDateTime(t.opened_at)}`,
    module: categoryToModule(t.category),
    customer: t.customer_name || '—',
    reference: t.order_id ? formatOrderId(t.order_id) : '—',
    created: formatDateTime(t.opened_at),
    slaDue: t.sla_due_at ? formatDateTime(t.sla_due_at) : '—',
    thread: (t.messages ?? []).filter((m) => m.sender_role !== 'system').map(adaptMessage),
    rawId: t.id,
    assignedToUserId: t.assigned_to_user_id,
    assignedToName: t.assigned_to_name || 'Unassigned',
  };
}

const CallCenterAllTickets: React.FC = () => {
  const items = useTicketsStore((s) => s.items);
  const current = useTicketsStore((s) => s.current);
  const loading = useTicketsStore((s) => s.loading);
  const error = useTicketsStore((s) => s.error);
  const saving = useTicketsStore((s) => s.saving);
  const fetchList = useTicketsStore((s) => s.fetchList);
  const fetchOne = useTicketsStore((s) => s.fetchOne);
  const update = useTicketsStore((s) => s.update);
  const reply = useTicketsStore((s) => s.reply);
  const setFilters = useTicketsStore((s) => s.setFilters);
  const resetFilters = useTicketsStore((s) => s.resetFilters);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [quickFilter, setQuickFilter] = useState<QuickFilter>(null);

  // Quick filters (VP-CS-001) go straight to the backend as real query
  // params rather than filtering the already-fetched page client-side,
  // since "unassigned"/"sla_breached"/assigned_to_user_id=me are resolved
  // server-side (see GET /tickets in the API). Re-runs (and refetches) any
  // time the active quick filter changes, including back to "All".
  useEffect(() => {
    resetFilters();
    if (quickFilter === 'mine') setFilters({ assigned_to_user_id: 'me' });
    else if (quickFilter === 'unassigned') setFilters({ unassigned: true });
    else if (quickFilter === 'urgent') setFilters({ priority: 'critical' });
    else if (quickFilter === 'sla') setFilters({ sla_breached: true });
    fetchList();
  }, [quickFilter, resetFilters, setFilters, fetchList]);

  // Default-select the first ticket on first load so the detail panel renders.
  useEffect(() => {
    if (!selectedId && items.length > 0) {
      setSelectedId(items[0].id);
    }
  }, [items, selectedId]);

  // Lazy-load the selected ticket (with messages) when selection changes.
  useEffect(() => {
    if (selectedId) fetchOne(selectedId).catch(() => undefined);
  }, [selectedId, fetchOne]);

  const allRows = useMemo<AllTicketsRow[]>(() => items.map(adaptRow), [items]);
  const detail = useMemo<TicketDetail | null>(() => (current ? adaptDetail(current) : null), [current]);

  const [statusFilter, setStatusFilter] = useState('All');
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [moduleFilter, setModuleFilter] = useState('All');
  const [dateFilter, setDateFilter] = useState('');
  const [staffFilter, setStaffFilter] = useState('');

  const rows = useMemo<AllTicketsRow[]>(() => {
    const staffQ = staffFilter.trim().toLowerCase();
    return allRows.filter((r) => {
      if (statusFilter !== 'All' && r.status !== statusFilter) return false;
      if (priorityFilter !== 'All' && r.priority !== priorityFilter) return false;
      if (moduleFilter !== 'All' && r.module !== moduleFilter) return false;
      if (staffQ && !r.assignedTo.toLowerCase().includes(staffQ)) return false;
      if (dateFilter) {
        // Match the source ticket's opened-at date against the picked date.
        const sourceTicket = items.find((t) => String(t.id) === r.id);
        if (sourceTicket) {
          const opened = sourceTicket.opened_at?.slice(0, 10);
          if (opened !== dateFilter) return false;
        }
      }
      return true;
    });
  }, [allRows, items, statusFilter, priorityFilter, moduleFilter, dateFilter, staffFilter]);

  return (
    <div className={styles.container}>
      <p className={styles.subtitle}>Master view of all support tickets across all modules</p>
      <FilterBar
        onNewTicket={() => setShowCreate(true)}
        status={statusFilter}
        onStatusChange={setStatusFilter}
        priority={priorityFilter}
        onPriorityChange={setPriorityFilter}
        module={moduleFilter}
        onModuleChange={setModuleFilter}
        date={dateFilter}
        onDateChange={setDateFilter}
        staff={staffFilter}
        onStaffChange={setStaffFilter}
        quickFilter={quickFilter}
        onQuickFilterChange={setQuickFilter}
      />
      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && rows.length === 0}
        emptyMessage="No tickets match the current filters"
        onRetry={() => fetchList()}
      >
        <TicketsTable rows={rows} onView={(id) => setSelectedId(parseInt(id, 10))} />
        {detail && (
          <TicketDetailPanel
            detail={detail}
            busy={saving}
            onEscalate={() => current && update(current.id, { priority: 'critical', status: 'in_progress' }).catch(() => undefined)}
            onResolve={() => current && update(current.id, { status: 'resolved' }).catch(() => undefined)}
            onUpdate={({ status }) => {
              if (!current) return Promise.resolve();
              // Map sidebar UI label → backend enum so PATCH /tickets/:id accepts it
              const map: Record<string, string> = {
                'Open': 'open',
                'In Progress': 'in_progress',
                'Waiting': 'waiting',
                'Resolved': 'resolved',
                'Closed': 'closed',
              };
              const backendStatus = map[status] ?? 'in_progress';
              return update(current.id, { status: backendStatus as any })
                .then(() => undefined)
                .catch(() => undefined);
            }}
            onAssign={(userId) => {
              if (!current) return Promise.resolve();
              return update(current.id, { assigned_to_user_id: userId })
                .then(() => undefined)
                .catch(() => undefined);
            }}
            onSendReply={(body) => current ? reply(current.id, body).then(() => undefined).catch(() => undefined) : Promise.resolve()}
          />
        )}
      </AsyncBoundary>
      <CreateTicketModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(id) => setSelectedId(id)}
      />
    </div>
  );
};

export const CallCenterAllTicketsPage: React.FC = () => <CallCenterAllTickets />;

export { CallCenterAllTickets };
export default CallCenterAllTickets;
